const MAX_EXPORT_DIMENSION = 1600;
const JPEG_QUALITY = 0.85;

export const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

function getRadianAngle(degreeValue) {
  return (degreeValue * Math.PI) / 180;
}

function rotateSize(width, height, rotation) {
  const rotRad = getRadianAngle(rotation);
  return {
    width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

function canvasToJpegBlob(canvas, quality = JPEG_QUALITY) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Görsel dışa aktarılamadı'));
      },
      'image/jpeg',
      quality
    );
  });
}

function scaleCanvasIfNeeded(sourceCanvas, maxDimension = MAX_EXPORT_DIMENSION) {
  const { width, height } = sourceCanvas;
  const longest = Math.max(width, height);
  if (longest <= maxDimension) {
    return sourceCanvas;
  }

  const scale = maxDimension / longest;
  const scaledCanvas = document.createElement('canvas');
  scaledCanvas.width = Math.round(width * scale);
  scaledCanvas.height = Math.round(height * scale);
  const ctx = scaledCanvas.getContext('2d');
  ctx.drawImage(sourceCanvas, 0, 0, scaledCanvas.width, scaledCanvas.height);
  return scaledCanvas;
}

export async function getRotatedFullImage(imageSrc, rotation = 0) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas bağlamı oluşturulamadı');
  }

  const rotRad = getRadianAngle(rotation);
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(image.width, image.height, rotation);

  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.translate(-image.width / 2, -image.height / 2);
  ctx.drawImage(image, 0, 0);

  const finalCanvas = scaleCanvasIfNeeded(canvas);
  return canvasToJpegBlob(finalCanvas);
}

/**
 * react-easy-crop crop alanından döndürülmüş/kırpılmış JPEG blob üretir.
 */
export async function getCroppedImg(imageSrc, pixelCrop, rotation = 0) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas bağlamı oluşturulamadı');
  }

  const rotRad = getRadianAngle(rotation);
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(image.width, image.height, rotation);

  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.translate(-image.width / 2, -image.height / 2);
  ctx.drawImage(image, 0, 0);

  const croppedCanvas = document.createElement('canvas');
  const croppedCtx = croppedCanvas.getContext('2d');
  if (!croppedCtx) {
    throw new Error('Kırpma canvas bağlamı oluşturulamadı');
  }

  croppedCanvas.width = pixelCrop.width;
  croppedCanvas.height = pixelCrop.height;

  croppedCtx.drawImage(
    canvas,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  const finalCanvas = scaleCanvasIfNeeded(croppedCanvas);
  return canvasToJpegBlob(finalCanvas);
}

export async function exportEditedImageFile(
  imageSrc,
  pixelCrop,
  rotation = 0,
  fileName = 'foto.jpg',
  mediaSize = null
) {
  let blob;
  if (mediaSize && pixelCrop) {
    const { width: fullW, height: fullH } = rotateSize(mediaSize.width, mediaSize.height, rotation);
    const coversFullImage =
      Math.abs(pixelCrop.width - fullW) <= 2 &&
      Math.abs(pixelCrop.height - fullH) <= 2 &&
      Math.abs(pixelCrop.x) <= 2 &&
      Math.abs(pixelCrop.y) <= 2;
    if (coversFullImage) {
      blob = await getRotatedFullImage(imageSrc, rotation);
    } else {
      blob = await getCroppedImg(imageSrc, pixelCrop, rotation);
    }
  } else {
    blob = await getCroppedImg(imageSrc, pixelCrop, rotation);
  }
  return new File([blob], fileName, { type: 'image/jpeg', lastModified: Date.now() });
}
